import { FilterOperatorEnum, Paged } from 'domains/appDomain';
import { getEffectiveUnit } from 'utils/measurement';
import { EquipmentsAvailablePress } from 'domains/eqptDomain';
import { BUSINESS_TYPE_ENUM } from 'domains/gallerySkuDomain';
import {
  JobConfigSvcOptionType,
  PlatformOptionPaperScope,
  PlatformSkuPaperUpdateParams,
} from 'domains/jobconfigDomain';
import {
  CreateSkuParam,
  InkFinishes,
  ListSkuParam,
  PartialSKU,
  PlatformSkuPaperOptions,
  SKU,
  SkuAddFilesPayload,
  SkuDynamicTemplateCategoryDTO,
  SkuDynamicTemplateDTO,
  SkuDynamicTemplateMappingDTO,
  SkuDynamicTemplateMappingSimpleDTO,
  SkuGalleryLabelsAll,
  SkuPaperOptions,
  SKUPart,
  SkuPresetSizes,
  SkuSizePairPayload,
  SkuType,
  UnitPriceBuyer,
  UnitPriceBuyerParams,
  UnitPriceSupplier,
  UnitPriceSupplierList,
  UnitPriceSupplierParams,
  UpdatePaper,
  UpdateSize,
  UpdateSkuPartParam,
} from 'domains/skuDomain';
import api from 'utils/api';
import { buildDocumentUrl, withMeasurementUnit } from 'utils/api';
import eqptService from './eqptService';
import gallerySkuService from './gallerySkuService';

const skuService = {
  getSkuTypes: () => {
    return api.get<SkuType[]>('/sku-types');
  },

  getPagedSkus: async (param: ListSkuParam) => {
    return api.post<Paged<SKU>>(
      `/api/skus/list?size=${param.page.pageSize}&page=${
        param.page.currentPage - 1
      }`,
      param.query,
      param.tenantId === undefined
        ? undefined
        : { 'X-Tenant-ID': param.tenantId }
    );
  },

  createSku: async (param: CreateSkuParam) => {
    return api.post<SKU>(
      `/api/skus?isMulti=${param.isMulti}${
        param.inheritFromId ? '&inheritFromId=' + param.inheritFromId : ''
      }${param.templateId ? '&templateId=' + param.templateId : ''}`
    );
  },

  startEditSku: async (sku: PartialSKU) => {
    return api.get<SKU>(`/api/skus/${sku.id}/edit`);
  },
  startViewSku: async (sku: PartialSKU, isFromView?: boolean) => {
    // Carries the unit the reader is working in, because it decides which ready-made size
    // catalogue comes back - a metric reader is offered A4 and A5, an imperial one Letter. The
    // API falls back to the owning org's system when nothing says.
    return api.get<SKU>(
      withMeasurementUnit(
        `/api/skus/${isFromView ? sku.id : sku.draftFromId || sku.id}/view`
      )
    );
  },
  updateSkuSummary: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/summary`, {
      ...sku,
      skuTypeId: sku?.skuType?.id,
    });
  },

  // TODO, will replace with actual APIs when backend is ready
  getCompanySkus: (pageNumber: number, skuTypeId?: number) => {
    return gallerySkuService.getGallerySkusByBusinessType(
      BUSINESS_TYPE_ENUM.PROMOTIONAL_ITEMS
    );
  },

  updateQuantities: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/quantities`, sku);
  },

  updateSize: async (sku: Partial<UpdateSize>) => {
    // State the unit these numbers are in rather than letting the API infer it from the request
    // header: the header is the unit being viewed now, and an open form keeps the values it was
    // filled with. Saving inches under a metric header divided every size by 25.4.
    //
    // The caller's own declaration wins, and the size steps make one: they state the unit their
    // fields are displaying. The reader's setting is only the fallback, for a payload assembled
    // outside any form.
    return api.put<SKU>(`/api/skus/${sku.id}/size`, {
      enteredUnit: getEffectiveUnit(),
      ...sku,
    });
  },

  /** Both catalogues of ready-made finished sizes; the size step shows whichever matches the unit. */
  getPresetSizes: async () => {
    return api.get<SkuPresetSizes>(`/api/skus/preset-sizes`);
  },

  getPaperOptions: async () => {
    return api.get<PlatformSkuPaperOptions>(`/api/materials/papers/options`);
  },

  getPaperCount: async (paperOption: SkuPaperOptions) => {
    return api.post<number>(`/api/materials/papers/count`, paperOption);
  },

  updatePaper: async (sku: UpdatePaper) => {
    return api.put<SKU>(`/api/skus/${sku.id}/paper-options`, sku);
  },

  getPlatformOptionPaper: async (skuId: number) => {
    return api.get<PlatformOptionPaperScope>(
      `/api/skus/${skuId}/option-papers`
    );
  },

  platformUpdatePaper: async (
    skuId: number,
    params: PlatformSkuPaperUpdateParams
  ) => {
    return api.put<SKU>(`/api/skus/${skuId}/paper`, params);
  },

  getInk: async () => {
    return api.get<InkFinishes>(`/api/equipments/ink-finishes`);
  },

  getGalleryLabels: async () => {
    return api.get<SkuGalleryLabelsAll>(`/api/skus/gallery-labels/all`);
  },

  updateInk: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/ink-and-coating`, sku);
  },

  getPresses: async (id: number) => {
    return api.get<EquipmentsAvailablePress>(
      `/api/equipments/available/press?skuId=${id}`
    );
  },

  updatePresses: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/presses`, sku);
  },

  updateSvc: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/svc`, sku);
  },

  updateExtraSpecs: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/extra-specs`, sku);
  },

  deleteSku: async (sku: PartialSKU) => {
    return api.delete<SKU>(`/api/skus/${sku.id}`);
  },

  removePart: async (sku: PartialSKU, part: SKUPart) => {
    return api.delete<SKU>(`/api/skus/${sku.id}/remove/${part.id}`);
  },

  addPartToMulti: async (sku: PartialSKU, newSku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/add-to-multi`, {
      skuId: newSku.id,
      version: newSku.version,
    });
  },

  updateSkuPartSequence: async (
    sku: PartialSKU,
    sequences: { partId: number; sequence: number }[],
    version: number
  ) => {
    return api.put<SKU>(`/api/skus/${sku.id}/sequence`, {
      sequences,
      version,
    });
  },

  updateSkuPartQuantities: async (param: UpdateSkuPartParam) => {
    return api.put<SKU>(`/api/skus/${param.skuId}/part/quantities`, param);
  },

  // updateSkuSvc: async (payload: { param: SvcGroup, showType: SvcShowTypeEnum, id?: number }) => {
  //   return api.put<SKU>(`/api/skus/${payload?.id}/services/${payload.showType}`, payload.param);
  // },

  getOptionSvcs: async (payload: { id?: number }) => {
    return api.get<JobConfigSvcOptionType>(
      `/api/skus/${payload?.id}/option-svc-group`
    );
  },

  updateSkuPartSizeBatch: async (sku: Partial<SkuSizePairPayload>) => {
    return api.put<SKU>(`/api/skus/${sku.wrapperSkuId}/part/size/batch`, sku);
  },

  publishSku: async (sku: PartialSKU) => {
    return api.put<SKU>(`/api/skus/${sku.id}/publish`);
  },

  skuAddFiles: async (payload: Partial<SkuAddFilesPayload>) => {
    return api.put<SKU>(`/api/skus/${payload.id}/add-files`, payload);
  },

  getExceptEqpts: async (skuItem: PartialSKU) => {
    const exceptIds = skuItem?.exceptEquipmentIds || [];
    if (exceptIds.length > 0) {
      const result = await eqptService.queryEqpts({
        page: {
          currentPage: 1,
          pageSize: skuItem?.exceptEquipmentIds?.length,
        },
        query: {
          id: {
            operator: FilterOperatorEnum.IN,
            values: skuItem?.exceptEquipmentIds || [],
          },
        },
      });

      return result.content;
    } else {
      return [];
    }
  },

  updateSkuActiveStatus: async (skuId: number, activeStatus: boolean) => {
    return api.put<SKU>(`/api/skus/${skuId}/active/${activeStatus}`);
  },

  getBuyerUnitPrice: (skuId: number) => {
    return api.get<UnitPriceBuyer>(`/skus/${skuId}/unit-price/buyer`);
  },

  updateBuyerUnitPrice: (
    skuId: number,
    params: UnitPriceBuyerParams,
    showError: boolean
  ) => {
    return api.post<UnitPriceBuyer>(
      `/skus/${skuId}/unit-price/buyer`,
      params,
      {},
      {
        useGlobalErrorHandler: showError,
        showOriginalError: true,
      }
    );
  },
  getBuyerUnitPriceExcel: (skuId: number): string => {
    return buildDocumentUrl(`/skus/${skuId}/unit-price/buyer/excel`);
  },
  getSupplierUnitPriceExcel: (skuId: number): string => {
    return buildDocumentUrl(`/skus/${skuId}/unit-price/supplier/excel`);
  },
  getSupplierUnitPrice: (skuId: number) => {
    return api.get<UnitPriceSupplier>(`/skus/${skuId}/unit-price/supplier`);
  },
  updateSupplierUnitPrice: (
    skuId: number,
    params: UnitPriceSupplierParams,
    showError: boolean
  ) => {
    return api.post<UnitPriceSupplier>(
      `/skus/${skuId}/unit-price/supplier`,
      params,
      {},
      {
        useGlobalErrorHandler: showError,
        showOriginalError: true,
      }
    );
  },
  getUnitPriceSupplierList: async (skuId: number, supplierPrice: boolean) => {
    return api.get<UnitPriceSupplierList[]>(
      `/skus/${skuId}/unit-price/suppliers`,
      { supplierPrice }
    );
  },
  getUnitPriceSupplierPricingEngine: async (
    skuId: number,
    params: {
      supplierOrgId: number;
      quantity: number;
    }
  ) => {
    return api.get<number>(
      `/skus/${skuId}/unit-price/supplier/pricing-engine`,
      params
    );
  },
  getUnitPricePart: async (skuId: number) =>
    api.get<boolean>(`/skus/${skuId}/unit-price/part`),

  getSkuDynamicMapping: async (skuId: number) =>
    api.get<SkuDynamicTemplateMappingDTO[]>(`/skus/dynamic-config/${skuId}`),

  getSkuDynamicTemplateCategory: async () =>
    api.get<SkuDynamicTemplateCategoryDTO[]>(
      `/skus/dynamic-config/template-categories`
    ),

  getSkuDynamicTemplateByCategoryId: async (categoryId: number) =>
    api.get<SkuDynamicTemplateDTO[]>(
      `/skus/dynamic-config/templates/${categoryId}`
    ),

  getSkuSimpleDynamicMapping: async (skuId: number) =>
    api.get<SkuDynamicTemplateMappingSimpleDTO>(
      `/skus/simple-dynamic-config/${skuId}`
    ),

  saveSkuSimpleDynamicMapping: async (
    dto: SkuDynamicTemplateMappingSimpleDTO
  ) => api.post<boolean>(`/skus/simple-dynamic-config`, dto),

  saveSkuDynamicTemplate: async (dto: SkuDynamicTemplateDTO) =>
    api.post<boolean>(`/skus/simple-dynamic-template`, dto),
};
export default skuService;
